import {ConfigService, events, RunConfigService, util} from '@nuskin/ns-util';
import {UserService, AccountManager} from './account.js';
import axios from 'axios';

const DOWNLINE_URL = RunConfigService.getBaseUrl() + "/vgservice/rest/downline-search";
const DOWNLINE_SEARCH_URL = RunConfigService.getBaseUrl() + "/vgservice/rest/downline-search";
const vgDistInfoUrl = RunConfigService.getBaseUrl() + "/vgservice/rest/vg-dist-info/${dist_id}";

let getPromise = null;
let retVGDistVal = null;

export default {
    getVGDistInfo,
    getVolumeData,
    formatVolumeData,
    checkVolumeData,
    getDownline,
    getVGDistInformation,
    searchDownline
}

/**
 * @typedef Qualification
 * @property {}
 *
 * Gets the qualifications for the logged in user
 * @returns {Promise<Object>}
 */
async function getVGDistInformation() {
    if (UserService.isLoggedIn()) {
        if (retVGDistVal == null) {
            try {
                if (!getPromise) {
                    getPromise = axios({
                        method: 'get',
                        url: _getDistInfoURL(),
                        headers: _getHeaders(),
                        responseType: 'json'
                    });
                }
                let response = await getPromise;
                retVGDistVal = response.data;
                getPromise = null;
            } catch (err) {
                retVGDistVal = {};
                // call failed
            }
        }
    }
    return retVGDistVal;
}


/**
 * Expects user to be logged in.
 * Will return vgService VG-Dist-Info
 * @returns {Promise.<{vgDistInfo}>} Promise that resolves to V&G Distributor Info
 */
async function getVGDistInfo(userDefinedConfig) {
    try {
        return await axios.request({
            method: 'get',
            url: _getDistInfoURL(),
            headers: _getHeaders(userDefinedConfig),
            responseType: 'json'
        });
    } catch (e) {
        console.error("vgDistInfoService exception: " , e);
    }
}

/**
 * Returns the downline of the authenticated user
 *
 * @returns {Promise<void>}
 */
async function getDownline(level) {
    if (UserService.isLoggedIn()) {
        let url = DOWNLINE_URL + "/" + UserService.getUser().id;
        if (level) {
            url += `?level=${level}`;
        }
        try {
            let response = await axios.request({
                method: 'get',
                url: url,
                headers: _getHeaders(),
                responseType:'json'
            });
            return response.data;
        } catch (e) {
            console.error("Failed to get downline: ", e);
            throw e;
        }
    } else {
        throw new Error("You must be logged in to get downline");
    }
}

/**
 * Searches the downline of the authenticated user by search term passed
 *
 * @returns {Promise<void>}
 */
async function searchDownline(searchTerm) {
    if (UserService.isLoggedIn()) {
        if(searchTerm && searchTerm.length >= 2) {
            let url = DOWNLINE_SEARCH_URL + "/" + UserService.getUser().id + "/" + searchTerm;
            try {
                let response = await axios.request({
                    method: 'get',
                    url: url,
                    headers: _getHeaders(),
                    responseType:'json'
                });
                return response.data;
            } catch (e) {
                console.error("Failed to get downline results: ", e);
                throw e;
            }
        } else {
            throw new Error("Search Term must be included and at least 2 characters");
        }
    } else {
        throw new Error("You must be logged in to get downline");
    }
}

/**
 * Expects user to be logged in.
 * Will format and return volumes data into an object
 * @returns {Promise.<{volumes}>}
 */
async function getVolumeData() {
    let vgDistInfo = await getVGDistInfo();
    return formatVolumeData(vgDistInfo.data.volume);
}

/**
 * Formats the volumes array into a JSON object
 * @param volumesArray
 * @returns {{}} Returns volumes object
 */
function formatVolumeData(volumesArray) {
    let volumes = {};
    if (typeof volumesArray === "undefined" || volumesArray.length === 0) {
        volumes.PSV = 0;
        volumes.GSV = 0;
        volumes.L1PSV = 0;
    } else {
        if (volumesArray && volumesArray.length > 0) {
            volumesArray.forEach((volume) => {
                volumes[volume.type] = volume.value;
            });
        }
    }

    return volumes;
}

/**
 * Will properly format the vgService VG-Dist-Info to the proper URL
 * @returns {string}
 * @private
 */
function _getDistInfoURL() {
    let user = UserService.getUser();
    if (UserService.isLoggedIn()) {
        return vgDistInfoUrl.replace("${dist_id}", user.id);
    } else {
        throw new Error("User not logged in");
    }
}

async function _loadVolumeData() {
    let distInfo = await getVGDistInformation();
    let volumeData = formatVolumeData(distInfo.volume);
    let _user = UserService.getUser();
    _user.volumes = volumeData;
    _user.highestRank = distInfo.highRank;
    _user.displayName = distInfo.displayName;
    _user.photoURL = distInfo.photoURL;
    _user.isVolumesLoaded = true;
    UserService.setUser(_user);
    events.publish(events.account.VOLUME_ADDED, [_user]);
    let pointsPromise = _lookupAvailablePoints();
    return pointsPromise;
}

function checkVolumeData() {
    return new Promise((resolve) => {
        if(!UserService.isVolumesLoaded()) {
            _loadVolumeData().then(function () {  // don't redirect until volume data loaded
                let pointsPromise = _lookupAvailablePoints();
                pointsPromise.always(function () {
                    events.publish(events.authentication.LOGIN_COMPLETE, [UserService.getUser()]);
                    resolve();
                });
            });
        } else {
            resolve();
        }
    });
}

function _lookupAvailablePoints() {
    let _config = ConfigService.getMarketConfig();
    let pointsPromise;
    if(_config.enablePointsRedemption){
        pointsPromise = $.ajax({
            type: "GET",
            url: _config.nsurl_sapAdrStatusService + AccountManager.currentData.id,
            data: {"countryGroup": util.countryCode, "eid": AccountManager.currentData.eid},
            contentType: "text/plain; charset=utf-8",
            beforeSend: function(xhr) {
                xhr.setRequestHeader("client_id", _config.checkout.clientId);
                xhr.setRequestHeader("client_secret", _config.checkout.clientSecret);
            }
        });
    } else {
        let deferred = new $.Deferred();
        deferred.resolve();
        return deferred;
    }

    pointsPromise.done(function(response) {
        // AEM-8414 rudist still has the login popup after logging in
        // Basically, there is a javascript error for ADRPoints is undefined
        // Need to check if ADRPoints does NOT exists before doing the loop
        if (response.ADRMemberStatusResponse && response.ADRMemberStatusResponse.ADRPoints) {
            $.each(response.ADRMemberStatusResponse.ADRPoints, function(key, value) {
                if(value.CountryCode === util.countryCode) {
                    let _user = UserService.getUser();
                    _user.availablePoints = value.AvailablePoints;
                    UserService.setUser(_user);
                }
            });
        }
    });

    // eslint-disable-next-line no-unused-vars
    pointsPromise.fail(function (error) {
        let _user = UserService.getUser();
        _user.availablePoints = 0;
        UserService.setUser(_user);
    });

    return pointsPromise;
}


/**
 * Returns headers needed for vgService VG-Dist-Info service call
 * @returns {{Accept: string, Content-Type: string, client_id: string, client_secret: string, client: string}}
 * @private
 */
function _getHeaders(userDefinedConfig) {
    let config;
    try {
        config = ConfigService.getMarketConfig();
    } catch(e) {
        config = userDefinedConfig;
    }
    let headers = {
        'Accept': 'application/json',
        'Content-Type': 'application/json',
        'client_id': config.checkout.clientId,
        'client_secret': config.checkout.clientSecret,
        'client': 'aem'
    }
    if (UserService.isLoggedIn()) {
        headers['eid'] = UserService.getUser().eid;
    }

    return headers;
}
